import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ByzMuiIconButton } from "../../core-components/mui/buttons/ByzMuiIconButton";
import ByzMuiButton from "../../core-components/mui/buttons/ByzMuiButton";
import ByzMuiLayoutButton from "../../core-components/mui/buttons/ByzMuiLayoutButton";
import { useTranslate } from "src/locales";
import { useState, useEffect } from "react";

interface PageCloseModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  content?: string;
  // Variant tipi: "one" = tek sekme kapanış, "full" = normal modal
  variant?: "one" | "full";
  // Kapatılacak sekme adı (variant="one" için)
  tabName?: string;
  // Custom button texts and actions
  yesButtonText?: string;
  noButtonText?: string;
  onDeny?: () => void;
}

const PageCloseModal = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  variant = "full",
  tabName,
  yesButtonText,
  noButtonText,
  onDeny,
}: PageCloseModalProps) => {
  const { t } = useTranslate();

  // Countdown timer state - 30 saniye
  const [countdown, setCountdown] = useState(30);

  // Countdown'u dakika:saniye formatına çevir
  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} ${t('page-close.seconds-short') || 'Sn.'}`;
  };

  // Süreyi uzat - 10 dakika (600 saniye) ekle
  const extendTime = () => {
    setCountdown(prev => prev + 600);
  };

  // Modal açıldığında countdown'u sıfırla ve başlat
  useEffect(() => {
    if (open) {
      setCountdown(30);
    }
  }, [open]);

  // Countdown timer
  useEffect(() => {
    if (!open || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Countdown bittiğinde otomatik onayla
          clearInterval(timer);
          handleConfirm();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, countdown]);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleDeny = () => {
    if (onDeny) {
      onDeny();
      onClose();
    } else {
      onClose();
    }
  };

  // "one" variant için özel render
  if (variant === "one") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '90%', sm: '400px', md: '540px' },
            height: { xs: 'auto', sm: '200px', md: '230px' },
            maxWidth: '540px',
            minHeight: { xs: '200px', sm: '200px', md: '230px' },
            borderRadius: 2,
            padding: 0,
            margin: { xs: 2, sm: 3 },
          }
        }}
      >
        {/* İçerik alanı - Responsive padding */}
        <Box sx={{ padding: { xs: '16px', sm: '20px', md: '24px' }, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

          {/* X Kapat butonu - Sağ üstte */}
          <ByzMuiIconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: { xs: '12px', md: '16px' },
              top: { xs: '12px', md: '16px' },
              padding: 0,
              width: { xs: '20px', md: '24px' },
              height: { xs: '20px', md: '24px' },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
          </ByzMuiIconButton>

          {/* Başlık ve alt başlık */}
          <Box sx={{ paddingRight: { xs: '28px', md: '32px' }, marginBottom: { xs: '16px', sm: '20px', md: '25px' } }}>
            {/* Ana başlık */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                lineHeight: '110%',
                letterSpacing: '0%',
                color: '#000',
                marginBottom: { xs: '8px', md: '12px' },
              }}
            >
              {title || t("page-close.selected-tab-title") || "Seçili Sekme Kapatılacak. Devam Etmek İstiyor musunuz?"}
            </Typography>

            {/* Alt açıklama - Sekme adı */}
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '12px', md: '14px' },
                lineHeight: '110%',
                letterSpacing: '0%',
                color: '#898989',
              }}
            >
              {content || `${tabName || t("page-close.your-screen") || "Ekranınız"} ${t("page-close.will-close") || "Kapanacaktır"}`}
            </Typography>
          </Box>

          {/* Ortak Alan - Countdown Timer */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Hayır ve Evet Butonları - Sağ üstte (Mobilde gizli) */}
            <Box sx={{ position: { xs: 'static', md: 'absolute' }, right: 0, top: 0, display: 'flex', gap: { xs: '8px', md: '10px' }, marginBottom: { xs: '12px', md: 0 }, justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
              <ByzMuiLayoutButton
                text={noButtonText || t("page-close.no") || "Hayır"}
                width="auto"
                height="auto"
                onClick={handleDeny}
                sx={{
                  minWidth: { xs: '55px', md: '63px' },
                  height: { xs: '36px', md: '44px' },
                  fontSize: { xs: '12px', md: '14px' },
                  padding: { xs: '8px 12px', md: '0' }
                }}
              />
              <ByzMuiLayoutButton
                text={yesButtonText || t("page-close.yes") || "Evet"}
                width="auto"
                height="auto"
                onClick={handleConfirm}
                sx={{
                  minWidth: { xs: '55px', md: '63px' },
                  height: { xs: '36px', md: '44px' },
                  fontSize: { xs: '12px', md: '14px' },
                  padding: { xs: '8px 12px', md: '0' }
                }}
              />
            </Box>

            {/* Countdown - Title */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '18px', md: '20px' },
                lineHeight: '110%',
                letterSpacing: '0%',
                color: '#000',
                marginBottom: { xs: '6px', md: '8px' },
              }}
            >
              {formatCountdown(countdown)}
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '12px', md: '14px' },
                lineHeight: '110%',
                letterSpacing: '0%',
                color: '#898989',
                marginBottom: { xs: '10px', md: '12px' },
              }}
            >
              {t("page-close.auto-close-warning") || "Sonra otomatik onaylayarak kapatılacaktır."}
            </Typography>

            {/* Süreyi Uzat Butonu */}
            <ByzMuiLayoutButton
              text={t("page-close.extend-time") || "Süreyi Uzat"}
              width="auto"
              height="auto"
              onClick={extendTime}
              sx={{
                width: { xs: '100px', md: '120px' },
                height: { xs: '18px', md: '20px' },
                fontSize: { xs: '11px', md: '14px' },
                padding: { xs: '4px 8px', md: '0' }
              }}
            />
          </Box>
        </Box>
      </Dialog>
    );
  }

  // "full" variant - Alt tab'lı üst tab için
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '90%', sm: '400px', md: '540px' },
          height: { xs: 'auto', sm: '190px', md: '210px' },
          maxWidth: '540px',
          minHeight: { xs: '180px', sm: '190px', md: '210px' },
          borderRadius: 2,
          padding: 0,
          margin: { xs: 2, sm: 3 },
        }
      }}
    >
      {/* İçerik alanı - Responsive padding */}
      <Box sx={{ padding: { xs: '16px', sm: '20px', md: '24px' }, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

        {/* X Kapat butonu - Sağ üstte */}
        <ByzMuiIconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: '12px', md: '16px' },
            top: { xs: '12px', md: '16px' },
            padding: 0,
            width: { xs: '20px', md: '24px' },
            height: { xs: '20px', md: '24px' },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
        </ByzMuiIconButton>

        {/* Başlık - Subtitle yok */}
        <Box sx={{ paddingRight: { xs: '28px', md: '32px' }, marginBottom: { xs: '16px', sm: '20px', md: '25px' } }}>
          {/* Ana başlık - Subtitle olmadan */}
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 700,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: '110%',
              letterSpacing: '0%',
              color: '#000',
            }}
          >
            {title || t("page-close.all-tabs-title") || "Tüm Sekme Kapatılacak. Devam Etmek İstiyor musunuz?"}
          </Typography>
        </Box>

        {/* Ortak Alan - Countdown Timer */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Hayır ve Evet Butonları - Sağ üstte */}
          <Box sx={{ position: { xs: 'static', md: 'absolute' }, right: 0, top: 0, display: 'flex', gap: { xs: '8px', md: '10px' }, marginBottom: { xs: '12px', md: 0 }, justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
            <ByzMuiLayoutButton
              text={noButtonText || t("page-close.no") || "Hayır"}
              width="auto"
              height="auto"
              onClick={handleDeny}
              sx={{
                minWidth: { xs: '55px', md: '63px' },
                height: { xs: '36px', md: '44px' },
                fontSize: { xs: '12px', md: '14px' },
                padding: { xs: '8px 12px', md: '0' }
              }}
            />
            <ByzMuiLayoutButton
              text={yesButtonText || t("page-close.yes") || "Evet"}
              width="auto"
              height="auto"
              onClick={handleConfirm}
              sx={{
                minWidth: { xs: '55px', md: '63px' },
                height: { xs: '36px', md: '44px' },
                fontSize: { xs: '12px', md: '14px' },
                padding: { xs: '8px 12px', md: '0' }
              }}
            />
          </Box>

          {/* Countdown - Title */}
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 700,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: '110%',
              letterSpacing: '0%',
              color: '#000',
              marginBottom: { xs: '6px', md: '8px' },
            }}
          >
            {formatCountdown(countdown)}
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '12px', md: '14px' },
              lineHeight: '110%',
              letterSpacing: '0%',
              color: '#898989',
              marginBottom: { xs: '10px', md: '12px' },
            }}
          >
            {t("page-close.auto-close-warning") || "Sonra otomatik onaylayarak kapatılacaktır."}
          </Typography>

          {/* Süreyi Uzat Butonu */}
          <ByzMuiLayoutButton
            text={t("page-close.extend-time") || "Süreyi Uzat"}
            width="auto"
            height="auto"
            onClick={extendTime}
            sx={{
              width: { xs: '100px', md: '120px' },
              height: { xs: '18px', md: '20px' },
              fontSize: { xs: '11px', md: '14px' },
              padding: { xs: '4px 8px', md: '0' }
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default PageCloseModal;

// End of component
